import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIdleTimeout } from './useIdleTimeout';
import { getIdleTimeoutMs } from '../../config/sessionPolicy';

/**
 * Idle session wiring for AuthProvider (extracted for the 500-line cap):
 * the role-based idle clock, the "Still there?" warning state and the
 * inactivity sign-out. Returns the value for `SessionWarningContext`.
 *
 * At 0:00 the REAL `signOut()` starts immediately (same scope/flags/timing as
 * before — never delayed for the pop-up's closing beat). `expired` makes the
 * pop-up play its normal exit and close; the login screen's inactivity notice
 * is the message (this path only — "Sign out now" and other expiry paths keep
 * their own notices).
 *
 * The timeout resolver is stable per role and reads the dev-only test switch
 * INSIDE (sessionPolicy), so toggling it re-resolves without a new identity;
 * the hook resets the clock only on a change of the resolved NUMBER.
 */
export default function useIdleSession({ enabled, role, isSuperAdmin, signOut, setAuthNotice }) {
  const [show, setShow] = useState(false);
  const [expired, setExpired] = useState(false);
  const expiringRef = useRef(false);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const timeoutMs = useCallback(() => getIdleTimeoutMs(role, isSuperAdmin), [role, isSuperAdmin]);

  useEffect(() => {
    if (enabled) {
      // A new session: drop anything an idle ending left behind (its exit beat
      // can be cut short by the session-tree teardown, so onExpiredDone may never run).
      if (expiringRef.current) { setShow(false); setExpired(false); }
      expiringRef.current = false;
      return;
    }
    // The session ended some OTHER way (other tab, kicked, refresh failure, 24h
    // cap) while the warning was up: never leave a stale dialog to greet the
    // next sign-in. The idle expiry's dialog clears itself via onExpiredDone after its exit.
    if (!expiringRef.current) { setShow(false); setExpired(false); }
  }, [enabled]);

  const onTimeout = useCallback(() => {
    if (expiringRef.current) return; // the 1s warning tick can fire again before the session is gone
    expiringRef.current = true;
    setExpired(true);
    // signOut() can settle WITHOUT ending the session (auth-js returns {error} on
    // a 5xx/network failure and keeps it). Give the SIGNED_OUT state a beat to
    // land, then: still signed in -> drop the ending and re-arm so the next idle
    // tick retries. The inactivity notice travels WITH signOut (set before the teardown, so
    // the sign-in card is born with it — no second heading flash, KAN-916); a retry drops it.
    const settled = () => setTimeout(() => {
      if (enabledRef.current) {
        expiringRef.current = false;
        setExpired(false);
        setShow(false);
        setAuthNotice(null);
      }
    }, 250);
    void Promise.resolve(signOut({ notice: { type: 'info', key: 'auth.notices.signedOutInactivity' } })).then(settled, settled);
  }, [signOut, setAuthNotice]);

  const clock = useIdleTimeout({
    enabled,
    timeoutMs,
    onWarning: () => setShow(true),
    onTimeout,
  });

  return useMemo(() => ({
    show: show || expired,
    expired,
    getRemainingMs: clock?.getRemainingMs,
    getLastActivityAt: clock?.getLastActivityAt,
    onStay: () => setShow(false),
    onSignOutNow: () => { setShow(false); signOut(); },
    // expiringRef stays set: the exit beat can end before signOut() settles, and
    // resetting it would let the 1s idle tick fire a second signOut().
    onExpiredDone: () => { setShow(false); setExpired(false); },
  }), [show, expired, clock?.getRemainingMs, clock?.getLastActivityAt, signOut]);
}
